import { useEffect, useState } from 'react';
import { useParams } from '../lib/routing';
import { HeaderContent, HEADER_TITLE_CLASS } from '../components/shell/app-header';
import { Modal, FormField } from '../components/modal';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  ArrowLeft, ArrowRight, Plus, EnvelopeSimple, ArrowsLeftRight, Pause, Play,
} from '@phosphor-icons/react';
import {
  getMyClassroomDetail,
  listMyClassroomStudentsPaginated,
  addMyStudent,
  setMyStudentActive,
  moveMyStudent,
  inviteMyStudentParent,
  listMyClassrooms,
  type ClassroomDetail,
  type Student,
  type Classroom,
} from '../lib/direction';

const PAGE_SIZE = 15;

function ActionBtn({
  onClick, title, children, className,
}: {
  onClick: () => void;
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className={cn(
        "grid place-items-center min-w-[36px] min-h-[36px] rounded-md bg-transparent border-none cursor-pointer text-[var(--naia-text-secondary)] hover:text-[var(--naia-text-primary)] hover:bg-accent transition-colors font-[inherit]",
        className,
      )}
    >
      {children}
    </button>
  );
}

export default function DirectionMemberClass() {
  const { id } = useParams<{ id: string }>();

  const [detail, setDetail] = useState<ClassroomDetail | null>(null);
  const [allClassrooms, setAllClassrooms] = useState<Classroom[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  type ModalKind = null | 'add' | 'move' | 'invite';
  const [modal, setModal] = useState<ModalKind>(null);
  const [saving, setSaving] = useState(false);
  const [addFirst, setAddFirst] = useState('');
  const [addLast, setAddLast] = useState('');
  const [addEmail, setAddEmail] = useState('');
  const [moveStudent_, setMoveStudent_] = useState<Student | null>(null);
  const [moveTarget, setMoveTarget] = useState('');
  const [inviteStudent, setInviteStudent] = useState<Student | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');

  const loadStudents = async (pg: number) => {
    if (!id) return;
    setStudentsLoading(true);
    try {
      const res = await listMyClassroomStudentsPaginated(id, pg, PAGE_SIZE);
      setStudents(res.students);
      setTotal(res.total);
    } catch { /* ignore */ } finally {
      setStudentsLoading(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([
      getMyClassroomDetail(id),
      listMyClassroomStudentsPaginated(id, 0, PAGE_SIZE),
      listMyClassrooms(),
    ])
      .then(([d, s, cls]) => {
        setDetail(d);
        setStudents(s.students);
        setTotal(s.total);
        setAllClassrooms(cls.filter((c) => !c.archived));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const goPage = (p: number) => { setPage(p); void loadStudents(p); };

  const openAdd = () => { setAddFirst(''); setAddLast(''); setAddEmail(''); setNotice(null); setModal('add'); };
  const submitAdd = async () => {
    if (!id || saving || (!addFirst.trim() && !addLast.trim())) return;
    setSaving(true);
    try {
      await addMyStudent({ classroomId: id, firstName: addFirst, lastName: addLast, email: addEmail.trim() || undefined });
      setModal(null);
      setPage(0);
      await loadStudents(0);
      if (detail) setDetail({ ...detail, total_count: detail.total_count + 1, active_count: detail.active_count + 1 });
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Échec de l'ajout.");
    } finally {
      setSaving(false);
    }
  };

  const openMove = (s: Student) => { setMoveStudent_(s); setMoveTarget(''); setNotice(null); setModal('move'); };
  const submitMove = async () => {
    if (!moveStudent_ || !moveTarget || saving) return;
    setSaving(true);
    try {
      await moveMyStudent(moveStudent_.id, moveTarget);
      setModal(null);
      await loadStudents(page);
      if (detail) setDetail({ ...detail, total_count: detail.total_count - 1, active_count: detail.active_count - (moveStudent_.active ? 1 : 0) });
    } catch (e) {
      setNotice(e instanceof Error ? e.message : 'Échec du déplacement.');
    } finally {
      setSaving(false);
    }
  };

  const openInvite = (s: Student) => { setInviteStudent(s); setInviteEmail(''); setNotice(null); setModal('invite'); };
  const submitInvite = async () => {
    const mail = inviteEmail.trim();
    if (!inviteStudent || !mail || saving) return;
    setSaving(true);
    try {
      await inviteMyStudentParent(mail, [inviteStudent.id]);
      setModal(null);
      setNotice(`Invitation envoyée à ${mail}.`);
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Échec de l'invitation.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (s: Student) => {
    setNotice(null);
    try {
      await setMyStudentActive(s.id, !s.active);
      setStudents((prev) => prev.map((x) => (x.id === s.id ? { ...x, active: !s.active } : x)));
      if (detail) {
        const delta = s.active ? -1 : 1;
        setDetail({ ...detail, active_count: detail.active_count + delta });
      }
    } catch (e) {
      setNotice(e instanceof Error ? e.message : 'Échec.');
    }
  };

  const otherClasses = allClassrooms.filter((c) => c.id !== id);
  const rangeStart = page * PAGE_SIZE + 1;
  const rangeEnd = Math.min((page + 1) * PAGE_SIZE, total);

  return (
    <>
      <HeaderContent
        width="md"
        showBurger
        backTo="/direction/mes-classes"
        backLabel="Mes classes"
        actions={detail && (
          <Button onClick={openAdd} size="sm" className="gap-1.5 shrink-0">
            <Plus size={13} weight="bold" /> Ajouter un élève
          </Button>
        )}
      >
        {detail && (
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className={HEADER_TITLE_CLASS}>{detail.name}</h1>
              {detail.level && (
                <Badge variant="secondary" className="text-[12px] font-bold">{detail.level}</Badge>
              )}
              <span className="text-[11px] font-extrabold text-primary bg-primary/10 rounded-full px-[10px] py-[3px]">
                {detail.active_count} actif{detail.active_count !== 1 ? 's' : ''}
              </span>
            </div>
            {detail.teachers.length > 0 && (
              <p className="m-0 mt-0.5 text-[13px] sm:text-[13.5px] leading-snug text-muted-foreground">
                {detail.teachers.map((t) => `${t.first_name} ${t.last_name}${t.subject_label ? ` (${t.subject_label})` : ''}`).join(' · ')}
              </p>
            )}
          </div>
        )}
      </HeaderContent>
      <main className="max-w-[880px] mx-auto px-5 pb-16 pt-4">

        {loading ? (
          <div className="flex flex-col gap-4">
            <Skeleton className="h-8 w-56 rounded-lg" />
            <Skeleton className="h-[300px] rounded-[var(--naia-r-card)]" />
          </div>
        ) : !detail ? (
          <p className="text-[var(--naia-text-secondary)] py-4">Classe introuvable ou hors périmètre.</p>
        ) : (
          <>

            {notice && (
              <p className="mb-4 px-[14px] py-[10px] rounded-[10px] bg-primary/10 text-primary text-sm font-semibold">
                {notice}
              </p>
            )}

            {/* Student list */}
            <div className="bg-[var(--naia-card)] border border-[var(--naia-line)] rounded-[var(--naia-r-card)] shadow-[var(--naia-shadow-card)] overflow-hidden">
              {studentsLoading ? (
                <p className="px-4 py-4 text-[13px] text-[var(--naia-text-secondary)]">Chargement…</p>
              ) : students.length === 0 ? (
                <p className="px-4 py-4 text-[13px] text-[var(--naia-text-secondary)]">Aucun élève. Ajoutez-en avec le bouton ci-dessus.</p>
              ) : (
                students.map((s, i) => (
                  <div
                    key={s.id}
                    className={cn(
                      "flex items-center gap-3 px-4 py-[11px] transition-colors",
                      i > 0 && "border-t border-[var(--naia-line)]",
                      !s.active && "opacity-50",
                    )}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-[14px] text-[var(--naia-text-primary)]">
                          {s.first_name} {s.last_name}
                        </span>
                        <span className={cn(
                          "text-[10.5px] font-extrabold rounded-full px-[9px] py-[2px]",
                          s.active
                            ? "text-primary bg-primary/10"
                            : "text-[var(--naia-text-secondary)] bg-[var(--naia-line)]",
                        )}>
                          {s.active ? 'Actif' : 'Inactif'}
                        </span>
                        <span className="text-[11px] text-[var(--naia-text-secondary)] font-mono">{s.code}</span>
                      </div>
                      {s.email && (
                        <div className="text-[12px] text-[var(--naia-text-secondary)] truncate mt-[1px]">{s.email}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-0 shrink-0">
                      <ActionBtn onClick={() => openInvite(s)} title="Inviter un parent / tuteur">
                        <EnvelopeSimple size={15} />
                      </ActionBtn>
                      {otherClasses.length > 0 && (
                        <ActionBtn onClick={() => openMove(s)} title="Déplacer dans une autre classe">
                          <ArrowsLeftRight size={15} />
                        </ActionBtn>
                      )}
                      <ActionBtn onClick={() => void toggleActive(s)} title={s.active ? 'Désactiver' : 'Réactiver'}>
                        {s.active ? <Pause size={15} /> : <Play size={15} />}
                      </ActionBtn>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Pagination */}
            {pageCount > 1 && (
              <div className="flex items-center justify-between mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 0}
                  onClick={() => goPage(page - 1)}
                  className="gap-1.5"
                >
                  <ArrowLeft size={13} /> Précédent
                </Button>
                <span className="text-[12.5px] text-[var(--naia-text-secondary)] font-semibold tabular-nums">
                  {rangeStart}–{rangeEnd} / {total} élève{total !== 1 ? 's' : ''}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= pageCount - 1}
                  onClick={() => goPage(page + 1)}
                  className="gap-1.5"
                >
                  Suivant <ArrowRight size={13} />
                </Button>
              </div>
            )}

            {pageCount === 1 && total > 0 && (
              <p className="text-[12px] text-[var(--naia-text-secondary)] mt-3 text-right tabular-nums">
                {total} élève{total !== 1 ? 's' : ''}
              </p>
            )}
          </>
        )}
      </main>

      <Modal
        open={modal === 'add'}
        title="Ajouter un élève"
        onClose={() => setModal(null)}
        footer={<>
          <Button variant="secondary" onClick={() => setModal(null)}>Annuler</Button>
          <Button onClick={submitAdd} disabled={saving || (!addFirst.trim() && !addLast.trim())}>
            {saving ? 'Ajout…' : 'Ajouter'}
          </Button>
        </>}
      >
        <FormField label="Prénom" value={addFirst} onChange={setAddFirst} placeholder="Prénom" autoFocus />
        <FormField label="Nom" value={addLast} onChange={setAddLast} placeholder="Nom" />
        <FormField label="Email (optionnel)" value={addEmail} onChange={setAddEmail} placeholder="email@exemple.fr" type="email" onEnter={submitAdd} />
      </Modal>

      <Modal
        open={modal === 'move'}
        title={`Déplacer ${moveStudent_?.first_name ?? ''} ${moveStudent_?.last_name ?? ''}`}
        onClose={() => setModal(null)}
        footer={<>
          <Button variant="secondary" onClick={() => setModal(null)}>Annuler</Button>
          <Button onClick={submitMove} disabled={saving || !moveTarget}>{saving ? 'Déplacement…' : 'Déplacer'}</Button>
        </>}
      >
        <p className="text-sm text-[var(--naia-text-secondary)] m-0 mb-3 leading-relaxed">
          Vers quelle classe déplacer{' '}
          <strong>{moveStudent_?.first_name} {moveStudent_?.last_name}</strong> ?
        </p>
        <div className="flex flex-wrap gap-2">
          {otherClasses.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setMoveTarget(c.id)}
              aria-pressed={moveTarget === c.id}
              className={cn(
                "inline-flex items-center min-h-[38px] rounded-full border-[1.5px] text-[13px] font-bold px-[14px] py-[6px] cursor-pointer font-[inherit] transition-colors",
                moveTarget === c.id
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-[var(--naia-line)] bg-transparent text-[var(--naia-text-secondary)] hover:border-primary",
              )}
            >
              {c.name}{c.level ? ` · ${c.level}` : ''}
            </button>
          ))}
        </div>
      </Modal>

      <Modal
        open={modal === 'invite'}
        title={`Inviter un parent / tuteur — ${inviteStudent?.first_name ?? ''} ${inviteStudent?.last_name ?? ''}`}
        onClose={() => setModal(null)}
        footer={<>
          <Button variant="secondary" onClick={() => setModal(null)}>Annuler</Button>
          <Button onClick={submitInvite} disabled={saving || !inviteEmail.trim()}>{saving ? 'Envoi…' : 'Inviter'}</Button>
        </>}
      >
        <p className="text-sm text-[var(--naia-text-secondary)] m-0 mb-3 leading-relaxed">
          Le parent / tuteur de{' '}
          <strong>{inviteStudent?.first_name} {inviteStudent?.last_name}</strong>{' '}
          recevra un lien pour créer son espace et suivre l'élève.
        </p>
        <FormField label="Email du parent / tuteur" value={inviteEmail} onChange={setInviteEmail} placeholder="parent@exemple.fr" type="email" autoFocus onEnter={submitInvite} />
      </Modal>
    </>
  );
}
